import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH_MODAL } from "@/constants/testIds";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Brand marks (lucide has no brand logos — small inline SVGs kept neutral/mono where possible).
const GoogleMark = (props) => (
  <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" {...props}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleMark = (props) => (
  <svg viewBox="0 0 384 512" width="16" height="16" aria-hidden="true" fill="currentColor" {...props}>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

export default function AuthModal() {
  const { customer, authModalOpen, setAuthModalOpen, login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("signin");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", password: "" });

  // Don't render for authenticated customers.
  if (customer) return null;

  const close = () => setAuthModalOpen(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginForm.email.trim().toLowerCase(), loginForm.password);
      toast.success("Welcome back");
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign in failed");
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (regForm.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await register({ ...regForm, email: regForm.email.trim().toLowerCase() });
      toast.success("Account created");
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const startGoogle = () => {
    setGoogleBusy(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]";

  return (
    <Dialog open={authModalOpen} onOpenChange={setAuthModalOpen}>
      <DialogContent
        data-testid={AUTH_MODAL.dialog}
        className="lch-public bg-[var(--panel)] border-[var(--border-2)] text-[var(--text)] sm:max-w-md p-0 overflow-hidden"
      >
        <div className="px-6 pt-6">
          <DialogHeader className="text-left">
            <div className="text-[#c9a86a] tracking-[0.3em] text-[10px] mb-1">CLIENT ACCESS</div>
            <DialogTitle className="text-xl font-semibold text-[var(--text)]">
              Le Collectif Horloger
            </DialogTitle>
            <DialogDescription className="text-[var(--muted)] text-sm">
              Sign in to track requests, orders, and your VIP tier.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 pt-4">
          {/* Social / SSO */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={startGoogle}
              disabled={googleBusy}
              data-testid={AUTH_MODAL.googleButton}
              className="w-full justify-center gap-2.5 bg-[var(--panel-2)] border-[var(--border-2)] text-[var(--text)] hover:bg-[var(--bg)] rounded-none h-11"
            >
              {googleBusy ? <Loader2 size={16} className="animate-spin" /> : <GoogleMark />}
              {googleBusy ? "Redirecting…" : "Continue with Google"}
            </Button>

            <Button
              type="button"
              variant="outline"
              disabled
              data-testid={AUTH_MODAL.appleButton}
              title="Coming soon"
              className="w-full justify-center gap-2.5 bg-[var(--panel-2)] border-[var(--border-2)] text-[var(--muted)] rounded-none h-11 cursor-not-allowed"
            >
              <AppleMark /> Continue with Apple
              <span className="ml-1 text-[10px] uppercase tracking-wider text-[var(--muted)]">Coming soon</span>
            </Button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-[var(--border-lch)]" />
            <span className="text-[10px] tracking-widest text-[var(--muted)]">OR WITH EMAIL</span>
            <span className="h-px flex-1 bg-[var(--border-lch)]" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full bg-[var(--panel-2)] rounded-none">
              <TabsTrigger value="signin" data-testid={AUTH_MODAL.tabSignin}
                className="rounded-none data-[state=active]:bg-[var(--bg)] data-[state=active]:text-[#c9a86a] text-[var(--text-2)]">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" data-testid={AUTH_MODAL.tabRegister}
                className="rounded-none data-[state=active]:bg-[var(--bg)] data-[state=active]:text-[#c9a86a] text-[var(--text-2)]">
                Create Account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-4">
              <form onSubmit={doLogin} className="space-y-4" data-testid="auth-signin-form">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[var(--text-2)]">Email</Label>
                  <Input type="email" required value={loginForm.email}
                    onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                    className={inputCls} data-testid={AUTH_MODAL.signinEmail} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[var(--text-2)]">Password</Label>
                  <Input type="password" required value={loginForm.password}
                    onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                    className={inputCls} data-testid={AUTH_MODAL.signinPassword} />
                </div>
                <Button type="submit" disabled={busy} data-testid={AUTH_MODAL.signinSubmit}
                  className="w-full bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none">
                  {busy ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-4">
              <form onSubmit={doRegister} className="space-y-4" data-testid="auth-register-form">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[var(--text-2)]">Full Name</Label>
                  <Input required value={regForm.name}
                    onChange={(e) => setRegForm((f) => ({ ...f, name: e.target.value }))}
                    className={inputCls} data-testid={AUTH_MODAL.registerName} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[var(--text-2)]">Email</Label>
                    <Input type="email" required value={regForm.email}
                      onChange={(e) => setRegForm((f) => ({ ...f, email: e.target.value }))}
                      className={inputCls} data-testid={AUTH_MODAL.registerEmail} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[var(--text-2)]">Phone</Label>
                    <Input value={regForm.phone}
                      onChange={(e) => setRegForm((f) => ({ ...f, phone: e.target.value }))}
                      className={inputCls} data-testid={AUTH_MODAL.registerPhone} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[var(--text-2)]">Password</Label>
                  <Input type="password" required value={regForm.password}
                    onChange={(e) => setRegForm((f) => ({ ...f, password: e.target.value }))}
                    className={inputCls} data-testid={AUTH_MODAL.registerPassword} />
                </div>
                <Button type="submit" disabled={busy} data-testid={AUTH_MODAL.registerSubmit}
                  className="w-full bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none">
                  {busy ? "Creating…" : "Create Account"}
                </Button>
                <p className="text-[11px] text-[var(--muted)] text-center">
                  Joined the VIP list with this email? Your tier carries over.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
